import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Tooltip,
    Button,
    Box
} from '@mui/material';
import styles from './MultiSelectWithInput.module.css';
import SnackBarMessage from '../snackbar/snackbar';
import { EraserIcon } from 'lucide-react';
import { CircularProgress } from "@mui/material";

const sortOptionsByContext = (options, label) => {
    const sortedOptions = [...options];

    if (label === 'Week') {
        sortedOptions.sort((a, b) => {
            const [fyA, weekA] = String(a).split('-');
            const [fyB, weekB] = String(b).split('-');
            return fyA === fyB
                ? Number.parseInt(weekA, 10) - Number.parseInt(weekB, 10)
                : fyA.localeCompare(fyB);
        });
        return sortedOptions;
    }

    if (typeof options?.[0] === 'number') {
        sortedOptions.sort((a, b) => a - b);
        return sortedOptions;
    }

    sortedOptions.sort((a, b) => {
        const strA = a != null ? a.toString() : '';
        const strB = b != null ? b.toString() : '';
        return strA.localeCompare(strB);
    });
    return sortedOptions;
};

const isRequiredAtLeastOneSelection = (type, label) => {
    if (type === 'Or Loss Tree' && label === 'Year') {
        return true;
    }
    return type === 'Daily Phasing' && (label === 'Week' || label === 'Plan' || label === 'UOM');
};

const shouldShowSelectAll = (type, label, isMultiSelect) => {
    if (!isMultiSelect) {
        return false;
    }

    const restrictedTypes = ['Daily Phasing', 'MM Screen Input', 'Or Loss Tree'];
    if (!restrictedTypes.includes(type)) {
        return true;
    }

    const restrictedLabels = ['Week', 'Plan', 'UOM', 'Data UOM'];
    if (!restrictedLabels.includes(label)) {
        return true;
    }

    return type === 'Or Loss Tree' && label === 'Week';
};

const normalizeSelectionArray = (initialValues, defaultValue, label) => {
    if (label === 'PO Ageing' && Array.isArray(initialValues) && initialValues.length > 0) {
        return [initialValues[initialValues.length - 1]];
    }

    if (Array.isArray(initialValues)) {
        return initialValues;
    }

    if (typeof initialValues === 'string' || typeof initialValues === 'number') {
        return [initialValues];
    }

    if (defaultValue !== null) {
        return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
    }

    return [];
};

const normalizeOrLossTreeYearSelection = (value) => {
    const valueCounts = {};
    for (const item of value) {
        const strVal = String(item);
        valueCounts[strVal] = (valueCounts[strVal] || 0) + 1;
    }

    const normalizedValue = [];
    const seen = new Set();
    for (const item of value) {
        const strVal = String(item);
        if (!seen.has(strVal)) {
            seen.add(strVal);
            if (valueCounts[strVal] === 1) {
                normalizedValue.push(strVal);
            }
        }
    }

    return normalizedValue;
};

const normalizeDraftValues = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value !== undefined && value !== null) {
        return [value];
    }

    return [];
};

const isSameOptionValue = (leftValue, rightValue) => String(leftValue) === String(rightValue);

const includesOptionValue = (values, option) => values.some((value) => isSameOptionValue(value, option));

const areAllFilteredOptionsSelected = (selectedValues, options) =>
    options.every((option) => includesOptionValue(selectedValues, option));

const removeFilteredOptionsFromSelection = (selectedValues, options) =>
    selectedValues.filter((value) => !includesOptionValue(options, value));

const EXCEL_LIKE_TYPES = ['RMPM Alert', 'Contract Alert', 'Contract PR2 Alert'];

const MultiSelectWithSearchInput = ({ label = "", options = [], filterValues = [], handleFilter = () => {}, disabled = false, isLabelShow = true, type = "", isMultiSelect = true, defaultValue = null }) => {
    const alertTypelist = ['Daily Phasing', 'MM Screen Input', 'RMPM Alert', 'Agile OR', 'Norm Alert',
        'Contract Alert', 'Contract PR2 Alert', 'Weekly OR', 'Supplier Insights', 'Manpower Shortage',
        'Or Loss Tree', 'Prioritization Matrix', 'Dispatch Priority', 'Hands Free VMI', 'PO Priority', 'INTR Recovery', 'Unloading Prioritisation', 'Unloading Prioritisation Shipment', 'In-Transit Recovery', 'In-Transit Recovery Shipment', 'Dispatch Prioritisation', 'Dispatch Prioritisation Shipment', 'Material View', 'Order To Allocation', 'Allocation To Delivery', 'Order To Delivery Hub', 'Smart Source Lens', 'Dc Supply Insights', 'User Access',
    'Deviation Tower'];
    const [selectedValues, setSelectedValues] = useState(() => {
        const initialValues = filterValues?.[label] || filterValues || [];
        return normalizeSelectionArray(initialValues, defaultValue, label);
    });
    const [draftSelectedValues, setDraftSelectedValues] = useState(() => {
        const initialValues = filterValues?.[label] || filterValues || [];
        return normalizeSelectionArray(initialValues, defaultValue, label);
    });
    const [menuOpen, setMenuOpen] = useState(false);
    const [hasUserTouchedSelection, setHasUserTouchedSelection] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options?.slice(0, options.length));
    const [labelTruncation, setLabelTruncation] = useState(false);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });
    const labelRef = useRef(null);
    const compRef = useRef(null);
    const ITEMS_PER_BATCH = 25;
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);
    const isExcelLikeEnabled = EXCEL_LIKE_TYPES.includes(type);

    const excludeFilterOptions = useMemo(() => {
        if (label === "Root cause" && type === "Contract Alert") {
            return options.filter(opt => opt !== "Contract Available");
        }

        if (label === "Root cause" && type === "Contract PR2 Alert") {
            return options.filter(opt => opt !== "Contract Available");
        }

        if (label === "Alert status" && ['RMPM Alert', 'Agile OR'].includes(type)) {
            return ['Open', 'Accepted', 'Rejected'];
        }
        if (label === "Alert status" && ['Contract Alert'].includes(type)) {
            return ['Open', 'Accepted', 'Rejected', 'Closed'];
        }

        return options;
    }, [options, label, type]);


    useEffect(() => {
        const initialValues = filterValues?.[label] || filterValues || [];

        setSelectedValues(() => {
            if (label !== 'PO Ageing' && Array.isArray(initialValues)) {
                const filtered = initialValues.filter(val =>
                    !(label === "Root cause" && type === "Contract Alert" && val === "Contract Available" && label === "Plan" && label === "Week" && label === "UOM") 
                );
                if (filtered.length === 0 && defaultValue !== null) {
                    return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
                }
                return filtered;
            }
            if (label === 'PO Ageing') {
                return normalizeSelectionArray(initialValues, defaultValue, label);
            }

            return normalizeSelectionArray(initialValues, defaultValue, label);
        });

        setDraftSelectedValues(() => {
            if (label !== 'PO Ageing' && Array.isArray(initialValues)) {
                const filtered = initialValues.filter(val =>
                    !(label === "Root cause" && type === "Contract Alert" && val === "Contract Available" && label === "Plan" && label === "Week" && label === "UOM")
                );
                if (filtered.length === 0 && defaultValue !== null) {
                    return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
                }
                return filtered;
            }
            if (label === 'PO Ageing') {
                return normalizeSelectionArray(initialValues, defaultValue, label);
            }

            return normalizeSelectionArray(initialValues, defaultValue, label);
        });
    }, [filterValues, label, excludeFilterOptions, type, defaultValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const filtered = excludeFilterOptions?.filter((opt) =>
                (typeof opt === 'string' && opt?.toLowerCase().includes(searchText?.toLowerCase())) ||
                (typeof opt === 'number' && opt?.toString().includes(searchText))
            );
            setFilteredOptions(searchText ? filtered?.slice(0, filtered.length) : excludeFilterOptions?.slice(0, excludeFilterOptions.length));
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchText, excludeFilterOptions]);

    // if label is truncated then only tooltip will show
    useEffect(() => {
        setLabelTruncation(labelRef.current.scrollWidth > labelRef.current.clientWidth)
    }, [label]);

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    const getNormalizedValue = useCallback((value) => {
        if (Array.isArray(value)) {
            return [...value].map(String).sort((a, b) => a.localeCompare(b));
        }

        if (value === undefined || value === null) {
            return [];
        }

        return [String(value)];
    }, []);

    const toggleOptionSelection = useCallback((option) => {
        setHasUserTouchedSelection(true);

        setDraftSelectedValues((prev) => {
            const prevValues = normalizeDraftValues(prev);

            if (!isMultiSelect) {
                return [option];
            }

            const isAlreadySelected = prevValues.some((val) => String(val) === String(option));

            if (label === 'PO Ageing') {
                return [option];
            }

            if (isAlreadySelected) {
                return prevValues.filter((val) => String(val) !== String(option));
            }

            return [...prevValues, option];
        });
    }, [isMultiSelect, label]);

    const getNextSelectionForOption = useCallback((currentSelection, option) => {
        const currentValues = normalizeDraftValues(currentSelection);

        if (!isMultiSelect) {
            return [option];
        }

        const isAlreadySelected = currentValues.some((val) => String(val) === String(option));

        if (label === 'PO Ageing') {
            return [option];
        }

        if (isAlreadySelected) {
            return currentValues.filter((val) => String(val) !== String(option));
        }

        return [...currentValues, option];
    }, [isMultiSelect, label]);

    const toggleSelectAll = useCallback(() => {
        setHasUserTouchedSelection(true);

        setDraftSelectedValues((prev) => {
            const prevValues = normalizeDraftValues(prev);
            const allFilteredSelected = areAllFilteredOptionsSelected(prevValues, filteredOptions);

            if (allFilteredSelected) {
                return removeFilteredOptionsFromSelection(prevValues, filteredOptions);
            }

            return [...new Set([...prevValues, ...filteredOptions])];
        });
    }, [filteredOptions]);

    const applySelection = useCallback((selection) => {
        let finalSelection = selection;

        if (type === 'Or Loss Tree' && label === 'Year') {
            finalSelection = normalizeOrLossTreeYearSelection(selection);
            if (finalSelection.length === 0) {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: `At least one option in ${label} is required.`,
                    type: "error",
                });
                return false;
            }
        }

        if (isRequiredAtLeastOneSelection(type, label) && finalSelection.length === 0) {
            setSnackBarObject({
                snackbarOpen: true,
                message: `At least one option in ${label} is required.`,
                type: "error",
            });
            return false;
        }

        setSelectedValues(finalSelection);
        handleFilter(label, isMultiSelect ? finalSelection : finalSelection[0]);
        return true;
    }, [type, label, handleFilter, isMultiSelect]);

    const handleApplyClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        const isApplied = applySelection(draftSelectedValues);
        if (isApplied) {
            setHasUserTouchedSelection(false);
            setMenuOpen(false);
        }
    }, [applySelection, draftSelectedValues]);

    const handleImmediateOptionClick = useCallback((option, event) => {
        event.preventDefault();
        event.stopPropagation();

        const nextSelection = getNextSelectionForOption(draftSelectedValues, option);
        const isApplied = applySelection(nextSelection);

        if (isApplied) {
            setDraftSelectedValues(nextSelection);
            setHasUserTouchedSelection(false);
        }
    }, [applySelection, draftSelectedValues, getNextSelectionForOption]);

    const handleImmediateSelectAllClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        const currentValues = normalizeDraftValues(draftSelectedValues);
        const allFilteredSelected = areAllFilteredOptionsSelected(currentValues, filteredOptions);
        const nextSelection = allFilteredSelected
            ? removeFilteredOptionsFromSelection(currentValues, filteredOptions)
            : [...new Set([...currentValues, ...filteredOptions])];

        const isApplied = applySelection(nextSelection);
        if (isApplied) {
            setDraftSelectedValues(nextSelection);
            setHasUserTouchedSelection(false);
        }
    }, [applySelection, draftSelectedValues, filteredOptions]);

    const handleOpen = useCallback(() => {
        setDraftSelectedValues(Array.isArray(selectedValues) ? [...selectedValues] : selectedValues);
        setHasUserTouchedSelection(false);
        setMenuOpen(true);
    }, [selectedValues]);

    const handleClose = useCallback(() => {
        setMenuOpen(false);
        setDraftSelectedValues(Array.isArray(selectedValues) ? [...selectedValues] : selectedValues);
        setHasUserTouchedSelection(false);
        setSearchText('');
    }, [selectedValues]);

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const handleMenuScroll = (event) => {
    const list = event.target;

    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 5) {
        setVisibleCount((prev) =>
        Math.min(prev + ITEMS_PER_BATCH, filteredOptions.length)
        );
    }
    };
    // Handle outside clicks
    const handleClickOutside = (event) => {
        if (compRef.current && !compRef.current.contains(event.target)) {
            setSearchText('');
        }
    };

    // Add event listener on mount
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            // Cleanup on unmount
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const clearValue = useCallback(() => {
        if (defaultValue !== null) {
            const resetValue = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue;
            setSelectedValues(resetValue);
            setDraftSelectedValues(resetValue);
            handleFilter(label, resetValue);
        } else {
            setSelectedValues([]);
            setDraftSelectedValues([]);
            handleFilter(label, []);
        }
        setSearchText('');
    }, [label, handleFilter, defaultValue]);

    const isAllSelected = filteredOptions.every(opt => {
        if (Array.isArray(draftSelectedValues)) {
            return draftSelectedValues.some(val => String(val) === String(opt));
        }
        return String(draftSelectedValues) === String(opt);
    }) && filteredOptions.length > 0;

    const isChecked = (opt) => {
        if (Array.isArray(draftSelectedValues)) {
            return draftSelectedValues.some(val => String(val) === String(opt));
        }
        return String(draftSelectedValues) === String(opt);
    };

    const hasAnyDraftSelection = useMemo(() => {
        if (Array.isArray(draftSelectedValues)) {
            return draftSelectedValues.length > 0;
        }
        return draftSelectedValues !== undefined && draftSelectedValues !== null;
    }, [draftSelectedValues]);

    const canApplyDraftSelection = hasUserTouchedSelection && hasAnyDraftSelection;

    const handleFooterMouseDown = useCallback((event) => {
        // Prevent menu blur/close while interacting with action footer.
        event.preventDefault();
        event.stopPropagation();
    }, []);

    // Check if current value is different from default value
    const isDifferentFromDefault = useCallback(() => {
        if (defaultValue === null) {
            return true;
        }

        return JSON.stringify(getNormalizedValue(defaultValue)) !== JSON.stringify(getNormalizedValue(selectedValues));
    }, [defaultValue, selectedValues, getNormalizedValue]);

    return (
        <>
            <div className={`${styles.labelFilterGroup} ${type === 'edit user' ? styles.editUser : ''}`} ref={compRef}>
                <Tooltip title={labelTruncation ? label : ''}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                color: '#4D4D4F',
                            }
                        }
                    }}
                    placement='top'
                >
                    <label ref={labelRef} className={styles.label} style={{ color: disabled ? "rgb(77 77 79 / 42%)" : '#4D4D4F' }}>
                        {isLabelShow ? label : ''}{alertTypelist.includes(type) && selectedValues.length > 0 && isDifferentFromDefault() && label!=='PO Ageing' && <EraserIcon className={styles.eraserIcon} onClick={clearValue} />}
                    </label>
                </Tooltip>
                <FormControl fullWidth>
                    <Select
                        id={`${label}-select`}
                        className={`${styles.filterFields} ${type === 'edit user' ? styles.filterFieldsUsers : ''}`}
                        multiple={isMultiSelect}
                        displayEmpty
                        value={draftSelectedValues}
                        open={menuOpen}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        disabled={disabled}
                        input={
                            <OutlinedInput />
                        }
                        style={{backgroundColor:'#fff'}}
                        renderValue={() => {
                            if (selectedValues.length === 0) {
                                return isLabelShow ? 'All' : label;
                            }

                            if (selectedValues.length === excludeFilterOptions.length && excludeFilterOptions.length > 1) {
                                return 'All';
                            }

                            return selectedValues.join(', ');
                        }}
                        MenuProps={{
                            autoFocus: false,
                            MenuListProps: {
                                sx: {
                                    paddingBottom: isExcelLikeEnabled ? 0 : '48px'
                                },
                            },
                            PaperProps: {
                                style: {
                                    maxHeight: '300px',
                                },
                                sx: {
                                    '&::-webkit-scrollbar': {
                                        width: '7px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '7px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        borderRadius: '7px',
                                    }
                                },
                            onScroll: handleMenuScroll,
                            },
                        }}
                    >
                        {label!=='PO Ageing' && (
                            <MenuItem key="search" sx={{ padding: 0, borderBottom: '1px solid #ccc', '&.MuiMenuItem-root:hover': { backgroundColor: 'transparent' } }}>
                                <input type="text"
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    className={styles.SearchInput}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    style={{ border: 'none', height: '32px', width: '100%', paddingLeft: '5px' }}
                                />
                            </MenuItem>)}
                        {shouldShowSelectAll(type, label, isMultiSelect) && label!=='PO Ageing' && 
                            <MenuItem value="Select all" sx={{
                                padding: '5px',
                                '& .MuiTypography-root': {
                                    fontSize: '12px',
                                },
                            }} onClick={(event) => {
                                if (isExcelLikeEnabled) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleSelectAll();
                                } else {
                                    handleImmediateSelectAllClick(event);
                                }
                            }}>
                                <Checkbox checked={isAllSelected} sx={{ padding: '2px' }} />
                                <ListItemText primary="Select all" />
                            </MenuItem>
                        }
                        {filteredOptions.slice(0, visibleCount).map((opt, ind) => {
                            return (
                                <MenuItem key={`${opt}_${ind}`} value={opt} sx={{
                                    padding: '5px',
                                    '& .MuiTypography-root': {
                                        fontSize: '12px',
                                    },
                                }} onClick={(event) => {
                                    if (isExcelLikeEnabled) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleOptionSelection(opt);
                                    } else {
                                        handleImmediateOptionClick(opt, event);
                                    }
                                }}>
                                    {!(label === 'Plan' || label === 'UOM' || (type === 'MM Screen Input' && label === 'Week') || (type === 'Or Loss Tree' && label === 'Data UOM')) &&
                                        <Checkbox checked={isChecked(opt)} sx={{ padding: '2px' }} />
                                    }
                                    <ListItemText primary={opt} />
                                </MenuItem>
                            );
                        })}
                        {visibleCount < filteredOptions.length && (                     
                        <MenuItem disabled sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={18} />
                        </MenuItem> 
                        )}
                        {isExcelLikeEnabled && (
                        <MenuItem
                            disableRipple
                            disableGutters
                            tabIndex={-1}
                            onMouseDown={handleFooterMouseDown}
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 2,
                                backgroundColor: '#ffffff',
                                borderTop: '1px solid #e0e0e0',
                                justifyContent: 'flex-end',
                                minHeight: '44px',
                                px: 1,
                                py: '2px',
                                '&.MuiMenuItem-root:hover': { backgroundColor: '#ffffff' }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleApplyClick}
                                    disabled={!canApplyDraftSelection}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: '56px',
                                        fontSize: '12px',
                                        minHeight: '32px',
                                        py: 0,
                                        backgroundColor: '#1F36C7',
                                        color: '#ffffff',
                                        '&:hover': {
                                            backgroundColor: '#1A2FA8'
                                        }
                                    }}
                                >
                                    OK
                                </Button>
                            </Box>
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </div>
            {snackBarObject["snackbarOpen"] && (
                <SnackBarMessage
                    type={snackBarObject["type"]}
                    snackbarMessage={snackBarObject["message"]}
                    snackbarOpen={snackBarObject["snackbarOpen"]}
                    setSnackbarOpen={closeSnackbar}
                />
            )
            }
        </>
    );
};

MultiSelectWithSearchInput.propTypes = {
    filterValues: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        PropTypes.object,
        PropTypes.string,
    ]),
    handleFilter: PropTypes.func,
    type: PropTypes.string,
    isMultiSelect: PropTypes.bool,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    isLabelShow: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    defaultValue: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        PropTypes.string,
        PropTypes.number,
    ]),
};

export default MultiSelectWithSearchInput;